import { useState } from 'react';

interface ManualPageCountCardProps {
  fileTypeLabel: string;
  defaultValue?: number;
  onConfirm: (count: number) => void;
}

/**
 * Shown instead of the full print-options layout when we can't determine a
 * page count in the browser (anything that isn't a PDF or image — Office
 * formats need a server-side renderer to page-count reliably). The person
 * confirms a count once; everything downstream (page range bounds, pricing)
 * then treats it exactly like a detected count.
 */
export function ManualPageCountCard({ fileTypeLabel, defaultValue = 1, onConfirm }: ManualPageCountCardProps) {
  const [draft, setDraft] = useState(defaultValue);

  return (
    <section className="bg-surface-lowest rounded-4 shadow-kiosk-sm p-3 p-lg-4 mt-3 d-flex flex-column flex-sm-row align-items-sm-center gap-3">
      <div
        className="rounded-3 bg-surface-high d-flex align-items-center justify-content-center text-primary flex-shrink-0"
        style={{ width: 44, height: 44 }}
      >
        <i className="bi bi-input-cursor-text" style={{ fontSize: 20 }} />
      </div>

      <div className="flex-grow-1">
        <p className="text-label-md text-on-surface mb-1">Enter page count</p>
        <p className="text-body-sm text-on-surface-variant mb-0">
          {fileTypeLabel} files can&apos;t be counted automatically in the browser yet — enter the number of
          pages so pricing and page-range selection are accurate.
        </p>
      </div>

      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        <input
          type="number"
          min={1}
          aria-label="Page count"
          className="form-control text-center"
          style={{ width: 88 }}
          value={draft}
          onChange={(e) => setDraft(Math.max(1, Math.round(Number(e.target.value) || 1)))}
        />
        <button
          type="button"
          className="btn btn-primary text-label-md active-press flex-shrink-0"
          onClick={() => onConfirm(draft)}
        >
          Confirm
        </button>
      </div>
    </section>
  );
}
